import { computed, reactive, ref, watch } from 'vue';

import { AuthGuestStore } from '@/auth/AuthGuestStore';
import { ApiClient } from '@/services/ApiClient';
import type { Resolved } from '@nzyme/ioc/Injectable.js';
import { defineService } from '@nzyme/ioc/Service.js';
import { Logger } from '@nzyme/logging/Logger.js';
import { useContainer } from '@nzyme/vue-ioc/useContainer.js';
import { ActorClient } from '@sigma/actors-client/ActorClient.js';
import { getPatientChatStateFold } from '@sigma/chat-common/utils/getPatientChatStateFold.js';
import { AuthStore } from '@sigma/client/auth/AuthStore.js';
import { useChatList } from '@sigma/client/chat/useChatList.js';
import { ChatGetEndpoint } from '@sigma/patient-api/chat/ChatGetEndpoint.js';
import type { ChatListEndpointResult } from '@sigma/patient-api/chat/ChatListEndpoint.js';
import { ChatListEndpoint } from '@sigma/patient-api/chat/ChatListEndpoint.js';
import { ChatMarkReadEndpoint } from '@sigma/patient-api/chat/ChatMarkReadEndpoint.js';

export type ChatListItem = ChatListEndpointResult;

export const ChatListStore = defineService({
    name: 'ChatListStore',
    deps: {
        api: ApiClient,
        authStore: AuthStore,
        guestStore: AuthGuestStore,
        actorClient: ActorClient,
        logger: Logger,
    },
    setup: ({ api, authStore, guestStore, actorClient, logger }) => {
        const chatList = useChatList<ChatListItem>({
            loadPage: async ({ beforeTimestamp, count }) => {
                return await api(ChatListEndpoint, { beforeTimestamp, count });
            },
            reloadChat: async chatId => {
                const result = await api(ChatGetEndpoint, { chatId });
                if (!result) {
                    return null;
                }
                return { ...result, hasMore: true };
            },
            foldState: (item, event) => {
                const newState = getPatientChatStateFold(item.state, event);
                if (!newState) {
                    return null;
                }
                return { ...item, state: newState, timestamp: Date.now(), hasMore: true };
            },
            syncReadMark: async (chatId, seq) => {
                await api(ChatMarkReadEndpoint, { chatId, seq });
            },
            actorClient,
            logger,
        });

        // Invalidate data when user changes
        watch(
            () => authStore.user?.id,
            () => {
                chatList.reset();
                void reload();
            },
        );
        watch(
            () => guestStore.guestId,
            () => {
                chatList.reset();
                void reload();
            },
        );

        return reactive({
            chats: computed(() => chatList.chats),
            hasMore: computed(() => chatList.hasMore),
            pending: computed(() => chatList.pending),
            reload,
            loadMore: chatList.loadMore,
            refresh: chatList.refresh,
            pushChat: chatList.pushChat,
            markRead: chatList.markRead,
            getReadSeq: chatList.getReadSeq,
        });

        function reload() {
            if (!guestStore.guestId && !authStore.user?.id) {
                chatList.reset();
                return undefined;
            }

            return chatList.reload();
        }
    },
});

export type ChatListStoreType = Resolved<typeof ChatListStore>;

export function mockChatListStore(chats: ChatListItem[]) {
    const chatsRef = ref(chats);

    const mock: ChatListStoreType = reactive({
        chats: chatsRef,
        hasMore: false,
        pending: false,
        reload: () => Promise.resolve(),
        loadMore: () => Promise.resolve(),
        refresh: () => Promise.resolve(),
        pushChat: () => {},
        markRead: () => {},
        getReadSeq: () => undefined,
    });

    useContainer().set(ChatListStore, mock);
}
